/**
 * Derives route-specific i18n namespace preload from registry entries + non-module routes.
 *
 * ## Data source
 *
 * Module routes: `registryEntries` from `appModuleDefinitions.js` (via entries re-export).
 * Public / legacy routes: `NON_MODULE_ROUTE_I18N` in this file.
 *
 * ## Resolution order (longest matching prefix wins)
 *
 * 1. `NON_MODULE_ROUTE_I18N` — public flows and legacy/dev URLs
 * 2. Registry `i18n.routeOverrides` — child paths with narrower namespace sets
 * 3. Registry `i18n.namespaces` — default per static prefix from `routePaths`
 *
 * Chunk preload is separate (`routeChunkLoaders.js`). This module only answers
 * "which JSON namespaces should load for this pathname?"
 *
 * Imported on login cold start — must not pull React, Ant Design, or react-icons.
 *
 * @module modules/appModuleI18n
 * @see docs/translate/preload.md
 */

import { registryEntries } from "./appModuleDefinitions";

/**
 * Lazy obligation drawer + AI widget namespaces.
 * Mirrors `EMBEDDED_COMPONENT_NAMESPACES` in `i18n.js` — duplicated here to avoid
 * a circular import (`i18n.js` imports this module).
 */
const EMBEDDED_WIDGET_NAMESPACES = ["obligationDetails", "ai"];

/** Namespace used for unauthenticated flows (login, external action login redirect). */
const PRE_LOGIN_NAMESPACES = ["login"];

/**
 * Route-prefix → namespace map for URLs **not** represented in module definitions.
 *
 * Includes:
 * - Public authenticated-external flows (`/login`, `/terms-and-conditions`, contract sessions, …)
 * - Legacy routes still mounted outside the module registry (`/fw2`, `/references`, `/test`)
 *
 * @type {Array<[string, string[]]>}
 */
export const NON_MODULE_ROUTE_I18N = [
  ["/terms-and-conditions", ["terms"]],
  ["/external/actions/login", PRE_LOGIN_NAMESPACES],
  ["/supplier/required/documents", ["contracts"]],
  ["/required/documents", ["contracts"]],
  ["/session/obligation", ["obligationDetails", "common"]],
  // Dev-only mock playground — longer than `/test` so longest-prefix match wins.
  ["/test/session/obligation", ["obligationDetails", "common"]],
  ["/external/upload", ["common"]],
  // Frameworks demo page — same namespaces as `/frameworks` but not a registry module route.
  ["/fw2", ["newFramework", "fromTemplate", ...EMBEDDED_WIDGET_NAMESPACES]],
  ["/references", ["references"]],
  ["/test", ["obligationsV2"]],
  ["/login", PRE_LOGIN_NAMESPACES],
];

/**
 * Derive a static URL prefix from a registry `routePaths` pattern.
 *
 * Parametric segments (`:frameworkId`, `:actionId`, …) are stripped so
 * `/frameworks/:frameworkId` and `/frameworks/abc` share the `/frameworks` i18n entry.
 * Concrete nested paths like `/entities/dashboards` are returned unchanged.
 *
 * @param {string} routePath — registry route pattern or concrete path
 * @returns {string} longest static prefix for i18n matching
 */
export function getStaticRoutePrefix(routePath) {
  const segments = String(routePath || "")
    .split("/")
    .filter(Boolean);

  const staticSegments = [];
  for (const segment of segments) {
    // Stop at the first dynamic segment — everything before is the preload prefix.
    if (segment.startsWith(":")) break;
    staticSegments.push(segment);
  }

  return staticSegments.length ? `/${staticSegments.join("/")}` : "/";
}

/**
 * Append embedded widget namespaces when a module opts in via `includeEmbeddedWidgets`.
 * Override entries use explicit `namespaces` only — they do not pass through this helper.
 *
 * @param {string[]} namespaces — base route namespaces from registry `i18n.namespaces`
 * @param {boolean} [includeEmbeddedWidgets]
 * @returns {string[]}
 */
function expandModuleNamespaces(namespaces, includeEmbeddedWidgets = false) {
  if (!includeEmbeddedWidgets) return [...namespaces];
  return [...namespaces, ...EMBEDDED_WIDGET_NAMESPACES];
}

/**
 * Flatten registry `i18n` config and `NON_MODULE_ROUTE_I18N` into a single lookup table.
 *
 * Build phases (order matters for deduplication, not for runtime match precedence —
 * runtime always sorts longest-prefix-first before matching):
 * 1. Non-module routes
 * 2. Per-module `i18n.routeOverrides` (exact paths, no embedded-widget expansion)
 * 3. Per-module defaults from `routePaths` static prefixes (skips prefixes claimed by overrides)
 *
 * @returns {Array<[string, string[]]>} sorted longest-prefix-first
 */
export function buildRouteNamespaceEntries() {
  const entries = [];
  /** Prefixes with explicit overrides — skip default emission for the same prefix. */
  const overridePaths = new Set();
  /** Dedupes `/frameworks` + `/frameworks/:id` emitting the same static prefix twice. */
  const emittedDefaultPrefixes = new Set();

  // Phase 1: public, legacy, and dev-only routes outside the module registry.
  for (const [prefix, namespaces] of NON_MODULE_ROUTE_I18N) {
    entries.push([prefix, [...namespaces]]);
  }

  // Phase 2: child-path overrides (e.g. `/entities/dashboards` → fewer namespaces).
  for (const moduleEntry of registryEntries) {
    const { i18n } = moduleEntry;
    if (!i18n) continue;

    for (const override of i18n.routeOverrides || []) {
      overridePaths.add(override.path);
      entries.push([override.path, [...override.namespaces]]);
    }
  }

  // Phase 3: default namespaces per static prefix derived from each module's routePaths.
  for (const moduleEntry of registryEntries) {
    const { i18n, routePaths } = moduleEntry;
    if (!i18n) continue;

    const defaultNamespaces = expandModuleNamespaces(
      i18n.namespaces,
      i18n.includeEmbeddedWidgets
    );

    for (const routePath of routePaths || []) {
      const prefix = getStaticRoutePrefix(routePath);
      if (overridePaths.has(prefix)) continue;
      if (emittedDefaultPrefixes.has(prefix)) continue;

      emittedDefaultPrefixes.add(prefix);
      entries.push([prefix, [...defaultNamespaces]]);
    }
  }

  // Longest prefix first — `/entities/dashboards` must win over `/entities` at match time.
  return entries.sort(([leftPrefix], [rightPrefix]) => rightPrefix.length - leftPrefix.length);
}

/**
 * Precomputed lookup table built once at module load.
 * @type {Array<[string, string[]]>}
 */
const routeNamespaceEntries = buildRouteNamespaceEntries();

/**
 * Read-only derived route map (replaces hand-maintained `ROUTE_NAMESPACES` in `i18n.js`).
 * Re-exported by `i18n.js` for tests and documentation audits.
 *
 * @type {ReadonlyArray<readonly [string, readonly string[]]>}
 */
export const ROUTE_NAMESPACES = Object.freeze(
  routeNamespaceEntries.map(([prefix, namespaces]) => [prefix, Object.freeze([...namespaces])])
);

/**
 * @param {string} [pathname]
 * @returns {string}
 */
const normalizePathname = (pathname = "/") => {
  const [pathWithoutQuery] = String(pathname || "/").split(/[?#]/);
  const normalized = pathWithoutQuery || "/";
  return normalized.startsWith("/") ? normalized : `/${normalized}`;
};

/**
 * @param {string} pathname
 * @param {string} prefix
 * @returns {boolean}
 */
const matchesRoutePrefix = (pathname, prefix) => (
  pathname === prefix || pathname.startsWith(`${prefix}/`)
);

/**
 * Returns route-specific namespaces for a pathname.
 *
 * Does **not** include `SHELL_NAMESPACES`, `notFound`, or public-route adjustments —
 * `getNamespacesForRoute()` in `i18n.js` layers those on top.
 *
 * @param {string} pathname — current or intended navigation URL
 * @returns {string[]} matching route namespaces, or `[]` when no prefix matches
 */
export function matchRouteNamespaces(pathname) {
  const normalizedPathname = normalizePathname(pathname);
  const match = routeNamespaceEntries.find(([prefix]) => matchesRoutePrefix(normalizedPathname, prefix));
  return match?.[1] || [];
}
